import React, { useContext } from "react";
import { Box, Text, ThemeContext } from "grommet";
import { normalizeColor } from "grommet/utils";
import { useRouter } from "next/router";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import styled from "styled-components";
import { formatCurrency, formatCurrencySI } from "../utils/numbers";
import { AGING_BALANCE_GRAPH_COLOR_MAPPING } from "../constants/Colors";
import { formatDate, formatTime, toLocalDateString } from "../utils/dates";
import { snakeCase } from "../utils/string";
import { agingReport, invoicesForAgingBucket } from "../utils/chart-data";
import Button from "./Button";
import { SkeletonBlock, SkeletonText } from "./charts/ChartSkeleton";
import ChartTooltip from "./charts/ChartTooltip";
import ChartLink from "./charts/ChartLink";
import SegmentBar from "./charts/SegmentBar";

const DonutWrap = styled.div`position: relative; width: 240px; height: 240px;`;
const DonutCenter = styled.div`
    position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
    pointer-events: none; text-align: center;
    a { pointer-events: auto; }
`;

const AgingColumns = styled.div`display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));`;

// Stand-ins while the summary loads, so the card keeps its height
const LOADING_BUCKETS = [["0", "Not yet due"], ["1", "1-30 days"], ["31", "31-60 days"], ["61", "61-90 days"], ["91", "91+ days"]]
    .map(([bucket, label]) => [bucket, {label, amount: 0}]);

const plural = (count, word) => `${count.toLocaleString("en-US")} ${word}${count === 1 ? "" : "s"}`;

// Past $1M the cents crowd the row and tell the reader nothing
const rowAmount = (amount, currency) => {
    const formatted = formatCurrency(amount, currency);
    return amount >= 100000000 ? formatted.replace(/\.\d+/, "") : formatted;
};

export const AgingBalanceRow = ({ bucket, agingBalance, color, currency, isLoading, href, section = "aging_balance_pie_chart" }) => {
    const router = useRouter();
    const { label, amount, invoice_count: count } = agingBalance;
    const row = (
        <Box direction="row" justify="between" align="center" pad={{vertical: "xsmall", horizontal: "xxsmall"}} gap="small">
            <Box direction="row" align="center" gap="xsmall">
                <Box round="100%" background={isLoading ? "chart-muted" : color} width={{min: "10px"}} height={{min: "10px"}} />
                <Text>{label}</Text>
            </Box>
            <Box direction="row" align="center" gap="small" flex={false}>
                {isLoading ? <SkeletonBlock width="88px" height="12px" /> : <Text>{rowAmount(amount, currency)}</Text>}
                {isLoading && <Box width={{min: "86px"}} align="end"><SkeletonBlock width="64px" height="12px" /></Box>}
                {count != null && (
                    <Box width={{min: "86px"}} align="end"><Text size="small" color="text-weak" style={{whiteSpace: "nowrap"}}>{plural(count, "invoice")}</Text></Box>
                )}
            </Box>
        </Box>
    );
    // Not a disabled button while loading, which would fade the placeholders
    if (isLoading) return row;
    return (
        <Button
            event={{section, element: `breakdown_${snakeCase(label)}`}}
            hoverIndicator
            onClick={() => router.push(href || invoicesForAgingBucket(bucket))}>
            {row}
        </Button>
    );
};

const AgingBalancesPieChart = ({ dashboardData, asOfDate, isLoading=true, wide }) => {
    const {
        aging_balances: agingBalances,
        total_receivables: totalAmount,
        invoice_count: invoiceCount,
        time_calculated: timeCalculated,
        currency,
    } = dashboardData || {};

    // aging_balances is keyed by age threshold, but also carries loose extras
    // such as `credits`, so keep only the entries shaped like an age bucket.
    //
    // The key travels with each slice as `bucket`, because that is what picks its colour. It
    // used to be picked by `name`, the backend's label, which is display copy: when "Due"
    // became "Not yet due" the lookup missed and the slice rendered with no fill at all.
    //
    // Bucket 0 used to be labelled "Due", for the money that is NOT due. The backend now sends
    // "Not yet due", but the company summary cache has no expiry, so a merchant can still be
    // served the old string indefinitely. Swapped here, once, so the legend, the slice name and
    // the slice's hover tooltip all read the same.
    const balanceBuckets = Object.entries(agingBalances || {})
        .filter(([, agingBalance]) => agingBalance?.label)
        .map(([bucket, agingBalance]) => [
            bucket,
            agingBalance.label === "Due" ? {...agingBalance, label: "Not yet due"} : agingBalance
        ]);
    const theme = useContext(ThemeContext);
    const router = useRouter();
    const isEmpty = !isLoading && !totalAmount;
    const slices = balanceBuckets
        .filter(([, agingBalance]) => agingBalance.amount > 0)
        .map(([bucket, agingBalance]) => ({
            name: agingBalance.label,
            bucket,
            value: agingBalance.amount,
            count: agingBalance.invoice_count,
        }));
    const data = totalAmount && slices.length > 0 ? slices : [{name: "placeholder", bucket: "placeholder", value: 1}];
    const hasData = data[0].bucket !== "placeholder";
    const report = agingReport(asOfDate ? toLocalDateString(asOfDate) : undefined);

    const renderTooltip = ({ active, payload }) => {
        const slice = payload?.[0]?.payload;
        if (!active || !slice) return null;
        return (
            <ChartTooltip
                title={slice.name}
                lines={[formatCurrency(slice.value, currency), ...(slice.count != null ? [plural(slice.count, "invoice")] : [])]}
            />
        );
    };

    return (
        <Box gap="small">
            {/* Wide, the total sits on the dashboard tile above, so it's a bar and a row of buckets */}
            {wide ? (
                <Box>
                    {/* With nothing owed there's no bar: the line below says so */}
                    {isLoading && <SkeletonBlock height="10px" />}
                    {!isLoading && hasData && (
                        <SegmentBar
                            segments={slices.map((slice) => ({
                                key: slice.bucket,
                                value: slice.value,
                                title: `${slice.name}: ${formatCurrency(slice.value, currency)}`,
                                color: normalizeColor(AGING_BALANCE_GRAPH_COLOR_MAPPING[slice.bucket], theme),
                            }))}
                        />
                    )}
                </Box>
            ) : (
            <Box align="center">
                <DonutWrap>
                    <PieChart width={240} height={240}>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={100}
                            outerRadius={116}
                            paddingAngle={data.length > 1 ? 3 : 0}
                            dataKey="value"
                            stroke="none"
                            isAnimationActive={false}
                            style={{cursor: hasData ? "pointer" : "default"}}
                            onClick={(slice) => hasData && router.push(asOfDate ? report : invoicesForAgingBucket(slice.payload?.bucket ?? slice.bucket))}>
                            {/* Keyed by bucket, not position, so a missing bucket can't shift the colours */}
                            {data.map((entry) => {
                                const color = isLoading ? "chart-muted" : AGING_BALANCE_GRAPH_COLOR_MAPPING[entry.bucket];
                                return <Cell key={entry.bucket} fill={normalizeColor(theme.global.colors[color] || color, theme)} />;
                            })}
                        </Pie>
                        {hasData && <Tooltip isAnimationActive={false} content={renderTooltip} />}
                    </PieChart>
                    <DonutCenter>
                        <Box align="center">
                            <Text color="text-weak">Total receivables</Text>
                            {isLoading ? <SkeletonText width="112px" height="28px" lineHeight="37px" /> : (
                                <ChartLink
                                    href={report}
                                    label={<Text size="32px" weight={600} color="text" style={{lineHeight: 1.15}}>{formatCurrencySI(totalAmount || 0, currency)}</Text>}
                                />
                            )}
                            {isLoading && <SkeletonText width="72px" lineHeight="20px" />}
                            {invoiceCount != null && <Text size="small" color="text-weak">{plural(invoiceCount, "invoice")}</Text>}
                        </Box>
                    </DonutCenter>
                </DonutWrap>
            </Box>
            )}
            {isEmpty ? (
                asOfDate
                    ? <Text color="text-weak">No open invoices on this date.</Text>
                    : (
                        <Box gap="small" align="start">
                            <Text color="text-weak">Open invoices appear here once you send one.</Text>
                            <Button
                                event={{section: "aging_balance_pie_chart", element: "create_invoice"}}
                                secondary
                                size="small"
                                label="Create invoice"
                                onClick={() => router.push("/draft-invoices/new/edit")}
                            />
                        </Box>
                    )
            ) : (
                wide && isLoading ? (
                    // The same grid as the loaded buckets, so nothing below moves when they arrive
                    <AgingColumns>
                        {LOADING_BUCKETS.map(([bucket]) => (
                            <Box key={bucket} pad="xsmall" gap="xxsmall">
                                <SkeletonText width="80px" />
                                <SkeletonText width="64px" height="18px" />
                                <SkeletonText width="56px" />
                            </Box>
                        ))}
                    </AgingColumns>
                ) : wide ? (
                    <AgingColumns>
                        {balanceBuckets.map(([bucket, agingBalance]) => (
                            <Button
                                key={bucket}
                                event={{section: "aging_balance_strip", element: `breakdown_${snakeCase(agingBalance.label)}`}}
                                hoverIndicator
                                onClick={() => router.push(asOfDate ? report : invoicesForAgingBucket(bucket))}>
                                <Box pad="xsmall" gap="xxsmall">
                                    <Box direction="row" align="center" gap="xsmall">
                                        <Box round="100%" background={AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket]} width={{min: "10px"}} height={{min: "10px"}} />
                                        <Text color="text-weak">{agingBalance.label}</Text>
                                    </Box>
                                    <Text size="large" weight={500}>{formatCurrencySI(agingBalance.amount || 0, currency)}</Text>
                                    {agingBalance.invoice_count != null && <Text size="small" color="text-weak">{plural(agingBalance.invoice_count, "invoice")}</Text>}
                                </Box>
                            </Button>
                        ))}
                    </AgingColumns>
                ) : (
                <Box>
                    {/* The legend dot comes from the same map as its slice, keyed by the same bucket */}
                    {(isLoading ? LOADING_BUCKETS : balanceBuckets).map(([bucket, agingBalance]) => (
                        <AgingBalanceRow
                            key={bucket}
                            bucket={bucket}
                            agingBalance={agingBalance}
                            color={AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket]}
                            currency={currency}
                            isLoading={isLoading}
                            href={asOfDate ? report : undefined}
                        />
                    ))}
                </Box>
                )
            )}
            <Box direction="row" justify="between" align="center" gap="small" wrap>
                {isLoading ? <SkeletonBlock width="180px" height="10px" /> : (
                    <Text color="text-weak" size="xsmall">
                        {timeCalculated ? `Last updated ${formatDate(timeCalculated)} ${formatTime(timeCalculated)}` : ""}
                    </Text>
                )}
                <ChartLink href={report} label={<Text size="small" color="text-weak">View report</Text>} />
            </Box>
        </Box>
    );
};

export default AgingBalancesPieChart;
